import React, { useEffect, useRef, useState } from "react";
import "../../styles/CSS/PortfolioItem.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Backdrop from "../Backdrop";
import Link from "../Link";
import { IconButton, ImageListItem, ImageList, Tooltip } from "@mui/material";
import FontAwesomeTextIcon from "../FontAwesomeTextIcon";
import {
  faDownLeftAndUpRightToCenter,
  faUpRightAndDownLeftFromCenter,
} from "@fortawesome/free-solid-svg-icons";
import {
  ArrowBack,
  ArrowBackIos,
  ArrowForward,
  ArrowForwardIos,
} from "@mui/icons-material";
import { useIntersectionObserver } from "../../util/IntersectionObserver";
import { trackButtonClick } from "../../util/analytics/ui";

function srcset(image, size, rows = 1, cols = 1) {
  if (size === null) size = 1;

  return {
    src: `${image}?w=${size * cols}&h=${size * rows}&fit=crop&auto=format`,
    srcSet: `${image}?w=${size * cols}&h=${size * rows
      }&fit=crop&auto=format&dpr=2 2x`,
  };
}

export const ImageGrid = ({
  images,
  layout = "condensed",
  onClick,
  children,
}) => {
  const [gridProps, setGridProps] = useState({
    cols: 1,
    rowHeight: 50,
    gap: 0,
  });

  const [curImage, setCurImage] = useState(-1);
  const imgContainerRef = useRef(null);

  useEffect(() => {
    if (Array.isArray(images)) {
      if (images.length <= 1) {
        setCurImage(0);
      }
      return;
    }

    if ("img" in images && images.data.length <= 1) {
      setCurImage(0);
    }

    setGridProps({
      cols: images.cols ?? 1,
      rowHeight: images.rowHeight ?? 50,
      gap: images.gap ?? 0,
    });
  }, [images]);

  useEffect(() => {
    if (layout === "modal") {
      setCurImage(0);
    }
  }, [layout]);

  useEffect(() => {
    if (!imgContainerRef.current || images === undefined) return;

    const imageList = Array.isArray(images)
      ? images
      : "data" in images
        ? images.data
        : [];

    const imageIndex = imageList.length > 1 ? curImage : 0;
    const currentImage = imageList[imageIndex];

    const img =
      typeof currentImage === "string" ? currentImage : currentImage?.url;

    if (img === undefined) return;

    imgContainerRef.current.style.setProperty("--image-url", `url(${img})`);
  }, [curImage, images]);

  if (!images) return null;

  const imageList = Array.isArray(images)
    ? images
    : "data" in images
      ? images.data
      : [];

  if (imageList.length === 0) return null;

  const handleContainerClick = (event) => {
    event.stopPropagation();
    if (onClick) onClick();
  };

  const handlePrevClick = (event) => {
    event.stopPropagation();
    setCurImage((cur) => {
      if (cur === -1) return imageList.length - 1;
      if (cur <= 0) return layout === "modal" ? imageList.length - 1 : -1;
      return cur - 1;
    });
  };

  const handleNextClick = (event) => {
    event.stopPropagation();
    setCurImage((cur) => {
      if (cur === imageList.length - 1) {
        if (layout === "modal") return 0;
        return -1;
      }
      return cur + 1;
    });
  };

  if (curImage === -1 && imageList.length > 1) {
    return (
      <div
        style={{ cursor: layout === "modal" ? "default" : "pointer" }}
        onClick={handleContainerClick}
      >
        <ImageList
          gap={gridProps.gap}
          sx={{
            borderRadius:
              layout === "condensed" ? "1em 0 0 1em" : "1em 1em 0 0",
            position: "relative",
            height: "100%",
            width: "100%",
          }}
          variant="quilted"
          cols={gridProps.cols}
          rowHeight={gridProps.rowHeight}
        >
          {layout === "modal" && (
            <div className="img-caro-buttons">
              <IconButton
                sx={{ background: "rgba(0,0,0,0.5)" }}
                onClick={handlePrevClick}
              >
                <ArrowBack />
              </IconButton>
              <IconButton
                sx={{ background: "rgba(0,0,0,0.5)" }}
                onClick={handleNextClick}
              >
                <ArrowForward />
              </IconButton>
            </div>
          )}
          {imageList.map((item, i) => {
            if (
              typeof item !== "string" &&
              "preview" in item &&
              item.preview === false
            ) {
              return null;
            }

            return (
              <ImageListItem
                key={typeof item === "string" ? item : item.url}
                cols={
                  typeof item !== "string" && item.cols ? item.cols : undefined
                }
                rows={
                  typeof item !== "string" && item.rows ? item.rows : undefined
                }
              >
                <img
                  {...srcset(
                    typeof item === "string" ? item : item.url,
                    typeof item !== "string" && item.size
                      ? item.size
                      : gridProps.rowHeight,
                    typeof item !== "string" && item.rows ? item.rows : 1,
                    typeof item !== "string" && item.cols ? item.cols : 1
                  )}
                  loading="lazy"
                />
              </ImageListItem>
            );
          })}
        </ImageList>
        {children}
      </div>
    );
  }

  const imageIndex = imageList.length > 1 ? curImage : 0;
  const currentImage = imageList[imageIndex];

  return (
    <div
      className={`portfolio-item__img ${layout} ${layout === "condensed" ? "fancy" : ""
        }`}
      style={{ cursor: layout === "modal" ? "default" : "pointer" }}
      onClick={handleContainerClick}
      ref={imgContainerRef}
    >
      {layout === "modal" && imageList.length > 1 && (
        <div className="img-caro-buttons">
          <IconButton
            sx={{ background: "rgba(0,0,0,0.5)" }}
            onClick={handlePrevClick}
          >
            <ArrowBack />
          </IconButton>
          <IconButton
            sx={{ background: "rgba(0,0,0,0.5)" }}
            onClick={handleNextClick}
          >
            <ArrowForward />
          </IconButton>
        </div>
      )}
      <img
        src={typeof currentImage === "string" ? currentImage : currentImage?.url}
        style={{
          borderRadius:
            layout === "modal"
              ? "none"
              : layout === "condensed"
                ? "1em 0 0 1em"
                : "1em 1em 0 0",
        }}
      />
      {children}
    </div>
  );
};

const PortfolioItem = ({
  item,
  onClick,
  onShowMore,
  onLinkClick,
  isModal,
  allowDynamicExpansion = true,
  onHover,
  isCondensed,
  onNavigate,
  items,
  ...others
}) => {
  const [state, setState] = useState({
    isCondensed: false,
  });

  const [overflowed, setOverflowed] = useState(false);

  const entireElem = useRef();
  const descriptionElem = useRef();
  const titleElem = useRef();
  const textContainerElem = useRef();

  function checkMulti() {
    if (!titleElem.current) return false;

    const ta = titleElem.current;

    ta.classList.add("nowrap");
    const multi = ta.scrollWidth > ta.offsetWidth;
    ta.classList.remove("nowrap");

    return multi;
  }

  const handleTextOverflow = () => {
    if (isModal || !item) return;

    if (titleElem.current && descriptionElem.current) {
      if (state.isCondensed) {
        descriptionElem.current.style.webkitLineClamp = 3;
        descriptionElem.current.style.lineClamp = 3;
      } else if (checkMulti()) {
        descriptionElem.current.style.webkitLineClamp = 4;
        descriptionElem.current.style.lineClamp = 4;
      } else {
        descriptionElem.current.style.webkitLineClamp = 3;
        descriptionElem.current.style.lineClamp = 3;
      }
    }

    if (
      descriptionElem.current &&
      descriptionElem.current.scrollHeight >
      descriptionElem.current.clientHeight
    ) {
      setOverflowed(true);
    } else {
      setOverflowed(false);
    }
  };

  useEffect(() => {
    if (isModal) return;
    if (!entireElem.current) return;

    entireElem.current.classList.remove("hidden-y-scale");

    if (state.isCondensed) {
      entireElem.current.classList.add("condensed");
    } else {
      entireElem.current.classList.remove("condensed");
    }
  }, [isModal, item, state.isCondensed]);

  useEffect(() => {
    if (item === undefined || item === null) return;
    setState((s) => ({ ...s, isCondensed }));
  }, [isCondensed, item]);

  useIntersectionObserver(entireElem, handleTextOverflow, () => { }, 0.25);

  if (item === undefined || item === null) return null;

  const handleRootClick = (event) => {
    event.stopPropagation();
    if (onClick) onClick(event);
  };

  const handleShowMoreClick = (event) => {
    event.stopPropagation();
    if (onShowMore) onShowMore(item);
  };

  const handleImageClick = () => {
    if (isModal) return;
    if (onShowMore) onShowMore(item);
  };

  const textElem = (
    <>
      <div className="portfolio-item__title" ref={titleElem}>
        <p>{item.title ? item.title : "Title"}</p>

        {!isModal && allowDynamicExpansion && (
          <IconButton
            onClick={(event) => {
              event.stopPropagation();
              setState((s) => ({ ...s, isCondensed: !s.isCondensed }));
              trackButtonClick(state.isCondensed ? "expand_portfolio_item" : "shrink_portfolio_item", {
                surface: "portfolio_item",
                context: state.isCondensed ? "expand_button" : "shrink_button",
                project: item.title ? item.title : "unknown",
              })
            }}
            size="small"
            sx={{ color: "white", alignSelf: "start" }}
          >
            <FontAwesomeTextIcon
              icon={
                state.isCondensed
                  ? faUpRightAndDownLeftFromCenter
                  : faDownLeftAndUpRightToCenter
              }
              text={state.isCondensed ? "Expand" : "Shrink"}
            />
          </IconButton>
        )}

        {isModal && item && items && (
          <div className="portfolio-item__nav">
            <Tooltip title="Previous Project">
              <IconButton
                onClick={(event) => {
                  event.stopPropagation();
                  if (onNavigate) onNavigate(-1);
                }}
              >
                <ArrowBackIos sx={{ color: "white" }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Next Project">
              <IconButton
                onClick={(event) => {
                  event.stopPropagation();
                  if (onNavigate) onNavigate(1);
                }}
              >
                <ArrowForwardIos sx={{ color: "white" }} />
              </IconButton>
            </Tooltip>
          </div>
        )}
      </div>

      {isModal && item.type && (item.time || item.date) && (
        <div className="portfolio-item__info">
          <p>{item.type}</p>
          <p style={{ margin: "0 .25em" }}> - </p>
          <p>{item.time || item.date}</p>
        </div>
      )}

      <div className="wrapper">
        <div
          className={
            isModal === undefined || isModal === false
              ? "portfolio-item__desc_less"
              : "portfolio-item__desc_all"
          }
          ref={descriptionElem}
        >
          {item.description ? item.description : "Project Description"}
        </div>

        {(isModal === undefined || isModal === false) && (
          <div
            style={{
              display: "inline-flex",
              pointerEvents: overflowed ? "auto" : "none",
            }}
          >
            <div
              className="desc_show_more"
              onClick={handleShowMoreClick}
              style={{
                opacity: overflowed ? 1 : 0,
                pointerEvents: overflowed ? "auto" : "none",
                display: overflowed ? "block" : "none",
              }}
            >
              More...
            </div>
          </div>
        )}
      </div>

      {item.icons && item.icons.length > 0 && (
        <div
          className="portfolio-item__icon"
          style={{
            display: state.isCondensed ? "block" : "inline-flex",
            textAlign: state.isCondensed ? "unset" : "left",
          }}
        >
          {item.icons.map((icon, i) => {
            if (icon.type === "font-awesome") {
              return (
                <FontAwesomeIcon
                  icon={icon.icon}
                  key={"portfolio-item-icon-" + i}
                />
              );
            }

            if (icon.type === "svg") {
              return <span key={"portfolio-item-icon-" + i}>{icon.icon}</span>;
            }

            return (
              <i
                className={`${icon.icon}`}
                key={"portfolio-item-icon-" + i}
              />
            );
          })}
        </div>
      )}

      {item.links && item.links.length > 0 && (
        <div className="portfolio-item__links">
          {item.links.map((link, i) => {
            return (
              <Link
                routerLink={link.routerLink}
                newTab={true}
                href={link.href}
                key={"portfolio-item-link-" + i}
                onClick={(event) => {
                  event.stopPropagation();
                  if (onLinkClick) onLinkClick(link, i);
                }}
              >
                {link.title}
              </Link>
            );
          })}
        </div>
      )}
    </>
  );

  return (
    <div
      ref={entireElem}
      onClick={handleRootClick}
      className={"portfolio-item" + (isModal ? " modal" : "") + " noselect"}
      {...others}
      onPointerEnter={() => {
        if (onHover) onHover(item);
      }}
    >
      {item.img && (
        <>
          {Array.isArray(item.img) || typeof item.img === "object" ? (
            <ImageGrid
              images={item.img}
              title={item.title}
              layout={
                isModal ? "modal" : state.isCondensed ? "condensed" : "card"
              }
              onClick={handleImageClick}
            />
          ) : (
            <div
              onClick={(event) => {
                event.stopPropagation();
                if (!isModal && onShowMore) onShowMore(item);
              }}
              className={`portfolio-item__img ${isModal ? "modal" : ""}`}
              style={{
                backgroundImage: `url(${item.img})`,
                cursor: isModal ? "default" : "pointer",
              }}
            />
          )}
        </>
      )}

      <div className="body-wrapper">
        {state.isCondensed ? (
          <div
            ref={textContainerElem}
            style={{ height: "100%", display: "flex", flexFlow: "column" }}
          >
            {textElem}
          </div>
        ) : (
          <>{textElem}</>
        )}
      </div>
    </div>
  );
};

export const ModalPortfolioItem = ({
  item,
  open,
  onNavigate,
  onLinkClick,
  items,
  handleClose,
}) => {
  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "scroll";

    return () => {
      document.body.style.overflow = "scroll";
    };
  }, [open]);

  return (
    <Backdrop
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 2,
        backgroundColor: "rgba(0,0,0,.5) !important",
      }}
      className={"portfolio-item-modal-backdrop"}
      open={open && item !== undefined && item !== null}
    >
      <PortfolioItem
        item={item}
        isModal={true}
        onNavigate={onNavigate}
        items={items}
        onLinkClick={(link, linkIndex) => {
          if (onLinkClick) onLinkClick(item, link, linkIndex);
        }}
      />
    </Backdrop>
  );
};

export default PortfolioItem;